<template>
  <div class="width-100">
    <h2 class="section-title">{{ $t('stability.timePointExport.title') }}</h2>

    <div class="form-block work-planning-form">
      <el-form
        class="operations-search-form"
        size="small"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <el-form-item>
              <span slot="label">{{
                $t('printAnalyses.customerNameFrom')
              }}</span>
              <el-autocomplete
                class="width-100"
                v-model="formData.customerNameFrom"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchCustomer({ text, cb, field: 'name', exact: false });
                  }
                "
                :trigger-on-focus="false"
                @input="resetCustomer('from', 'name')"
                @select="(item) => selectCustomer(item, 'from')"
                @change="(item) => emptySelectedCustomer(item, 'from')"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </el-form-item>
          </el-col>

          <el-col :span="4">
            <el-form-item>
              <span slot="label">{{
                $t('printAnalyses.customerCodeFrom')
              }}</span>
              <el-autocomplete
                class="width-100"
                v-model="formData.customerCodeFrom"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchCustomer({ text, cb, field: 'id', exact: true });
                  }
                "
                :trigger-on-focus="false"
                @input="resetCustomer('from', 'code')"
                @select="(item) => selectCustomer(item, 'from')"
                @change="(item) => emptySelectedCustomer(item, 'from')"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </el-form-item>
          </el-col>

          <el-col :span="8">
            <el-form-item>
              <span slot="label">{{ $t('printAnalyses.customerNameTo') }}</span>
              <el-autocomplete
                class="width-100"
                v-model="formData.customerNameTo"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchCustomer({ text, cb, field: 'name', exact: false });
                  }
                "
                :trigger-on-focus="false"
                @input="resetCustomer('to', 'name')"
                @select="(item) => selectCustomer(item, 'to')"
                @change="(item) => emptySelectedCustomer(item, 'to')"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </el-form-item>
          </el-col>

          <el-col :span="4">
            <el-form-item>
              <span slot="label">{{ $t('printAnalyses.customerCodeTo') }}</span>
              <el-autocomplete
                class="width-100"
                v-model="formData.customerCodeTo"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchCustomer({ text, cb, field: 'id', exact: true });
                  }
                "
                :trigger-on-focus="false"
                @input="resetCustomer('to', 'code')"
                @select="(item) => selectCustomer(item, 'to')"
                @change="(item) => emptySelectedCustomer(item, 'to')"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.acceptedFrom')">
              <el-date-picker
                v-model="formData.acceptedFrom"
                :picker-options="fromDateOptions('acceptedTo')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.acceptedTo')">
              <el-date-picker
                v-model="formData.acceptedTo"
                :picker-options="toDateOptions('acceptedFrom')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.analysisEndFrom')">
              <el-date-picker
                v-model="formData.analysisEndFrom"
                :picker-options="fromDateOptions('analysisEndTo')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.analysisEndTo')">
              <el-date-picker
                v-model="formData.analysisEndTo"
                :picker-options="toDateOptions('analysisEndFrom')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.analysisDueFrom')">
              <el-date-picker
                v-model="formData.analysisDueFrom"
                :picker-options="fromDateOptions('analysisDueTo')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.analysisDueTo')">
              <el-date-picker
                v-model="formData.analysisDueTo"
                :picker-options="toDateOptions('analysisDueFrom')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.commodityCode')">
              <el-select
                v-model="formData.commodityCode"
                placeholder=""
                @change="updateStoredForm"
                clearable
              >
                <el-option
                  v-for="laboratory in laboratories"
                  :key="laboratory"
                  :value="laboratory"
                  :label="$t(`constants.${laboratory}`)"
                />
              </el-select>
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.sampleCodeFrom')">
              <el-input
                v-model="formData.sampleReportCodeFrom"
                clearable
                @change="updateStoredForm"
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.sampleCodeTo')">
              <el-input
                v-model="formData.sampleReportCodeTo"
                clearable
                @change="updateStoredForm"
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.analysisCode')">
              <el-autocomplete
                v-model="analysisCodeBuffer"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchAnalyses({ text, cb, field: 'code' });
                  }
                "
                :trigger-on-focus="false"
                @select="selectAnalysis"
              >
                <template slot-scope="{ item }">
                  <AutocompleteAnalysis
                    :item="item"
                    :showIcon="false"
                  />
                </template>
              </el-autocomplete>
              <FilterTags
                :filter-data="selectedAnalysisCodes"
                :filter="{ name: 'analysisCodes' }"
                @tagClosed="
                  ({ item }) => {
                    removeAnalysisCode(item);
                  }
                "
              />
            </FormItem>
          </el-col>

          <el-col :span="8">
            <FormItem :label="$t('printAnalyses.analysisDescription')">
              <el-autocomplete
                v-model="analysisDescriptionBuffer"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchAnalyses({ text, cb, field: 'description' });
                  }
                "
                :trigger-on-focus="false"
                @select="selectAnalysis"
              >
                <template slot-scope="{ item }">
                  <AutocompleteAnalysis :item="item" />
                </template>
              </el-autocomplete>
              <FilterTags
                :filter-data="selectedAnalysisDescriptions"
                :filter="{ name: 'analysisDescriptions' }"
                @tagClosed="
                  ({ item }) => {
                    removeAnalysisCode(item);
                  }
                "
              />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.methodDescription')">
              <el-autocomplete
                v-model="formData.methodDescription"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchMethod(text, cb);
                  }
                "
                :trigger-on-focus="false"
                @select="addMethodData"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
              <FilterTags
                :filterData="formData.methodTags"
                :filter="{ name: 'method' }"
                @tagClosed="
                  ({ item }) => {
                    removeMethodData(item);
                  }
                "
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.sampleStatus')">
              <el-select
                placeholder=""
                value=""
                @change="addStatus"
                clearable
              >
                <el-option
                  v-for="status in LAB_SAMPLE_STATUSES"
                  :key="status"
                  :value="status"
                  :label="
                    $t(
                      `enums.sampleStatuses.${LAB_SAMPLE_STATUSES_DESC[status]}`
                    )
                  "
                />
              </el-select>
              <FilterTags
                :filter-data="selectedAnalysesStatuses"
                :filter="{ name: 'labSampleStatus' }"
                @tagClosed="
                  ({ item }) => {
                    removeStatus(item);
                  }
                "
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.orderId')">
              <el-input
                v-model="formData.orderId"
                type="number"
                @change="addOrderIdData"
              />
              <FilterTags
                :filterData="formData.orderIdsTags"
                :filter="{ name: 'orderId' }"
                @tagClosed="
                  ({ item }) => {
                    removeOrderIdData(item);
                  }
                "
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.instrument')">
              <el-autocomplete
                v-model="formData.analysisBrandsDescriptions"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchBrand(text, cb);
                  }
                "
                :trigger-on-focus="false"
                @select="addAnalysisBrand"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
              <FilterTags
                :filterData="formData.analysisBrandsTags"
                :filter="{ name: 'analysisBrands' }"
                @tagClosed="
                  ({ item }) => {
                    removeAnalysisBrand(item);
                  }
                "
              />
            </FormItem>
          </el-col>

          <el-col :span="8">
            <el-form-item>
              <span slot="label">{{
                $t('printAnalyses.finalCustomerName')
              }}</span>
              <el-autocomplete
                class="width-100"
                v-model="formData.finalCustomer"
                value-key="label"
                clearable
                :fetch-suggestions="
                  (text, cb) => {
                    searchCustomer({ text, cb, field: 'name', exact: false });
                  }
                "
                :trigger-on-focus="false"
                @select="(item) => selectCustomer(item, 'final')"
                @change="(item) => emptySelectedCustomer(item, 'final')"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.groupingId')">
              <el-input
                v-model.number="formData.groupingId"
                type="number"
                @change="addGroupingIdData"
              />
              <FilterTags
                :filterData="formData.groupingIdsTags"
                :filter="{ name: 'groupingId' }"
                @tagClosed="
                  ({ item }) => {
                    removeGroupingIdData(item);
                  }
                "
              />
            </FormItem>
          </el-col>

          <el-col :span="8">
            <FormItem :label="$t('printAnalyses.location')">
              <el-autocomplete
                v-model="formData.locationName"
                :fetch-suggestions="locationsList"
                :trigger-on-focus="false"
                value-key="label"
                clearable
                placeholder=""
                @input="resetLocation"
                @select="selectLocation"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.accreditedPharma')">
              <el-select
                v-model="formData.accreditedPharma"
                placeholder=""
                @change="updateStoredForm"
                clearable
              >
                <el-option
                  v-for="item in accreditedPharmaList"
                  :key="item.value"
                  :value="item.value"
                  :label="item.label"
                />
              </el-select>
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.signedAtFrom')">
              <el-date-picker
                v-model="formData.signedAtFrom"
                :picker-options="fromDateOptions('signedAtTo')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('printAnalyses.signedAtTo')">
              <el-date-picker
                v-model="formData.signedAtTo"
                :picker-options="toDateOptions('signedAtFrom')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="4">
            <FormItem :label="$t('stability.timePointExport.completedFrom')">
              <el-date-picker
                v-model="formData.completedFrom"
                :picker-options="fromDateOptions('completedTo')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>

          <el-col :span="4">
            <FormItem :label="$t('stability.timePointExport.completedTo')">
              <el-date-picker
                v-model="formData.completedTo"
                :picker-options="toDateOptions('completedFrom')"
                @change="updateStoredForm"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
              />
            </FormItem>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="primary"
            native-type="submit"
            @click.prevent="exportExcel"
          >
            {{ $t('stability.timePointExport.export') }}
          </el-button>
        </FormButtons>
      </el-form>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import fileSaver from 'file-saver';
  import { mapGetters, mapActions } from 'vuex';
  import pick from 'lodash.pick';
  import pickBy from 'lodash.pickby';
  import FormItem from '@/components/FormItem.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteAnalysis from '@/components/AutocompleteAnalysis.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import FilterTags from '@/components/FilterTags.vue';
  import {
    laboratories,
    LAB_SAMPLE_STATUSES,
    LAB_SAMPLE_STATUSES_DESC,
  } from '@/config/constants';
  import { stabilityApi } from '@/api';
  import analysisFilterSuggestionsMixin from '@/mixins/analysisFilterSuggestionsMixin';

  const FORM_KEY = 'stabilityTimePointExport';
  const MAX_DB_INTEGER = 2147483647;

  const initialFormData = () => ({
    customerNameFrom: '',
    customerCodeFrom: '',
    customerFromId: null,
    customerNameTo: '',
    customerCodeTo: '',
    customerToId: null,
    acceptedFrom: '',
    acceptedTo: '',
    analysisEndFrom: '',
    analysisEndTo: '',
    analysisDueFrom: '',
    analysisDueTo: '',
    commodityCode: null,
    sampleReportCodeFrom: '',
    sampleReportCodeTo: '',
    analysisCodesDescriptions: [],
    labSampleStatus: [],
    analysisBrands: [],
    analysisBrandsDescriptions: '',
    analysisBrandsTags: [],
    method: [],
    methodDescription: '',
    methodTags: [],
    orderId: '',
    orderIds: [],
    orderIdsTags: [],
    finalCustomer: '',
    groupingId: null,
    groupingIds: [],
    groupingIdsTags: [],
    accreditedPharma: null,
    locationId: null,
    locationName: '',
    signedAtFrom: '',
    signedAtTo: '',
    completedFrom: '',
    completedTo: '',
  });

  const CUSTOMER_FIELDS = {
    from: {
      id: 'customerFromId',
      name: 'customerNameFrom',
      code: 'customerCodeFrom',
    },
    to: { id: 'customerToId', name: 'customerNameTo', code: 'customerCodeTo' },
  };

  const apiObjectKeys = [
    'customerFromId',
    'customerToId',
    'acceptedFrom',
    'acceptedTo',
    'analysisEndFrom',
    'analysisEndTo',
    'analysisDueFrom',
    'analysisDueTo',
    'commodityCode',
    'sampleReportCodeFrom',
    'sampleReportCodeTo',
    'analysisCodes',
    'method',
    'labSampleStatus',
    'orderIds',
    'analysisBrands',
    'finalCustomer',
    'groupingIds',
    'accreditedPharma',
    'locationId',
    'signedAtFrom',
    'signedAtTo',
    'completedFrom',
    'completedTo',
  ];

  export default {
    name: 'StabilityTimePointExport',
    mixins: [analysisFilterSuggestionsMixin],
    components: {
      FormItem,
      FormButtons,
      AutocompleteAnalysis,
      AutocompleteItem,
      FilterTags,
    },
    data() {
      return {
        formData: initialFormData(),
        analysisCodeBuffer: '',
        analysisDescriptionBuffer: '',
        laboratories,
        LAB_SAMPLE_STATUSES,
        LAB_SAMPLE_STATUSES_DESC,
        accreditedPharmaList: [
          {
            label: this.$t('printAnalyses.noAccredited'),
            value: 'no accredited',
          },
          { label: this.$t('printAnalyses.accredited'), value: 'accredited' },
          { label: this.$t('printAnalyses.pharma'), value: 'pharma' },
        ],
      };
    },
    computed: {
      ...mapGetters(['forms']),
      selectedAnalysesStatuses() {
        return this.formData.labSampleStatus.map((labSampleStatus) => ({
          label: this.$t(
            `enums.sampleStatuses.${LAB_SAMPLE_STATUSES_DESC[labSampleStatus]}`
          ),
          key: labSampleStatus,
        }));
      },
      selectedAnalysisCodes() {
        return this.formData.analysisCodesDescriptions.map((item, index) => ({
          label: item.code,
          key: index,
        }));
      },
      selectedAnalysisDescriptions() {
        return this.formData.analysisCodesDescriptions.map((item, index) => ({
          label: item.description,
          key: index,
        }));
      },
    },
    methods: {
      ...mapActions(['updateForm']),
      updateStoredForm() {
        this.updateForm({ formKey: FORM_KEY, formData: this.formData });
      },
      fromDateOptions(toKey) {
        return {
          disabledDate: (date) =>
            !!this.formData[toKey] &&
            moment(date).format('YYYY-MM-DD') > this.formData[toKey],
        };
      },
      toDateOptions(fromKey) {
        return {
          disabledDate: (date) =>
            !!this.formData[fromKey] &&
            moment(date).format('YYYY-MM-DD') < this.formData[fromKey],
        };
      },
      selectAnalysis(item) {
        if (
          !this.formData.analysisCodesDescriptions.some(
            (current) => current.value === item.value
          )
        ) {
          this.formData.analysisCodesDescriptions.push(item);
        }
        this.updateStoredForm();
      },
      removeAnalysisCode(item) {
        this.formData.analysisCodesDescriptions.splice(item.key, 1);
        this.updateStoredForm();
      },
      resetCustomer(field, source) {
        const keys = CUSTOMER_FIELDS[field];
        if (!this.formData[keys.id]) {
          return;
        }
        this.formData[keys.id] = null;
        this.formData[source === 'name' ? keys.code : keys.name] = '';
        this.updateStoredForm();
      },
      selectLocation(item) {
        this.formData.locationId = item.value;
        this.formData.locationName = item.label;
        this.updateStoredForm();
      },
      resetLocation() {
        if (!this.formData.locationId) {
          return;
        }
        this.formData.locationId = null;
        this.updateStoredForm();
      },
      selectCustomer(item, field) {
        if (field === 'from') {
          this.formData.customerFromId = item.value;
          this.formData.customerNameFrom = item.customerName;
          this.formData.customerCodeFrom = item.code;
        }
        if (field === 'to') {
          this.formData.customerToId = item.value;
          this.formData.customerNameTo = item.customerName;
          this.formData.customerCodeTo = item.code;
        }
        if (field === 'final') {
          this.formData.finalCustomer = item.customerName;
        }
        this.updateStoredForm();
      },
      emptySelectedCustomer(text, field) {
        if (text !== '') {
          return;
        }
        if (field === 'from') {
          this.formData.customerFromId = null;
          this.formData.customerNameFrom = '';
          this.formData.customerCodeFrom = '';
        }
        if (field === 'to') {
          this.formData.customerToId = null;
          this.formData.customerNameTo = '';
          this.formData.customerCodeTo = '';
        }
        if (field === 'final') {
          this.formData.finalCustomer = '';
        }
        this.updateStoredForm();
      },
      addStatus(item) {
        if (!this.formData.labSampleStatus.includes(item)) {
          this.formData.labSampleStatus.push(item);
        }
        this.updateStoredForm();
      },
      removeStatus(item) {
        this.formData.labSampleStatus = this.formData.labSampleStatus.filter(
          (status) => status !== item.key
        );
        this.updateStoredForm();
      },
      addMethodData(item) {
        if (!this.formData.method.includes(item.label)) {
          this.formData.method.push(item.label);
          this.formData.methodTags.push(item);
        }
        this.formData.methodDescription = '';
        this.updateStoredForm();
      },
      removeMethodData(item) {
        this.formData.method = this.formData.method.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.methodTags = this.formData.methodTags.filter(
          (arrayItem) => arrayItem.key !== item.key
        );
        this.updateStoredForm();
      },
      parseId(value, labelKey) {
        if (value === '' || value === null) {
          return null;
        }
        const id = Number(value);
        if (Number.isInteger(id) && id > 0 && id <= MAX_DB_INTEGER) {
          return id;
        }
        this.$notify({
          showClose: true,
          type: 'warning',
          message: this.$t('stability.timePointExport.invalidId', {
            field: this.$t(labelKey),
          }),
        });
        return null;
      },
      addOrderIdData(value) {
        this.formData.orderId = '';
        const id = this.parseId(value, 'printAnalyses.orderId');
        if (id === null) {
          return;
        }
        if (!this.formData.orderIds.includes(id)) {
          this.formData.orderIds.push(id);
          this.formData.orderIdsTags.push({ key: id, label: id });
        }
        this.updateStoredForm();
      },
      removeOrderIdData(item) {
        this.formData.orderIds = this.formData.orderIds.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.orderIdsTags = this.formData.orderIdsTags.filter(
          (arrayItem) => arrayItem.key !== item.key
        );
        this.updateStoredForm();
      },
      addGroupingIdData(value) {
        this.formData.groupingId = null;
        const id = this.parseId(value, 'printAnalyses.groupingId');
        if (id === null) {
          return;
        }
        if (!this.formData.groupingIds.includes(id)) {
          this.formData.groupingIds.push(id);
          this.formData.groupingIdsTags.push({ key: id, label: id });
        }
        this.updateStoredForm();
      },
      removeGroupingIdData(item) {
        this.formData.groupingIds = this.formData.groupingIds.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.groupingIdsTags = this.formData.groupingIdsTags.filter(
          (arrayItem) => arrayItem.key !== item.key
        );
        this.updateStoredForm();
      },
      addAnalysisBrand(item) {
        if (!this.formData.analysisBrands.includes(item.label)) {
          this.formData.analysisBrands.push(item.label);
          this.formData.analysisBrandsTags.push(item);
        }
        this.formData.analysisBrandsDescriptions = item.label;
        this.updateStoredForm();
      },
      removeAnalysisBrand(item) {
        this.formData.analysisBrands = this.formData.analysisBrands.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.analysisBrandsTags =
          this.formData.analysisBrandsTags.filter(
            (arrayItem) => arrayItem.key !== item.key
          );
        if (this.formData.analysisBrandsDescriptions === item.label) {
          this.formData.analysisBrandsDescriptions = '';
        }
        this.updateStoredForm();
      },
      prepareFormData() {
        const data = pick(
          {
            ...this.formData,
            analysisCodes: this.formData.analysisCodesDescriptions.map(
              (item) => item.code
            ),
          },
          apiObjectKeys
        );
        return pickBy(data, (value) =>
          Array.isArray(value) ? value.length > 0 : value
        );
      },
      unconfirmedFilterLabels() {
        const { formData } = this;
        const hasText = (value) => !!String(value || '').trim();
        return [
          [
            'printAnalyses.customerNameFrom',
            hasText(formData.customerNameFrom) && !formData.customerFromId,
          ],
          [
            'printAnalyses.customerCodeFrom',
            hasText(formData.customerCodeFrom) && !formData.customerFromId,
          ],
          [
            'printAnalyses.customerNameTo',
            hasText(formData.customerNameTo) && !formData.customerToId,
          ],
          [
            'printAnalyses.customerCodeTo',
            hasText(formData.customerCodeTo) && !formData.customerToId,
          ],
          ['printAnalyses.analysisCode', hasText(this.analysisCodeBuffer)],
          [
            'printAnalyses.analysisDescription',
            hasText(this.analysisDescriptionBuffer),
          ],
          [
            'printAnalyses.methodDescription',
            hasText(formData.methodDescription),
          ],
          [
            'printAnalyses.instrument',
            hasText(formData.analysisBrandsDescriptions) &&
              !formData.analysisBrands.includes(
                formData.analysisBrandsDescriptions
              ),
          ],
          [
            'printAnalyses.location',
            hasText(formData.locationName) && !formData.locationId,
          ],
        ]
          .filter(([, pending]) => pending)
          .map(([labelKey]) => this.$t(labelKey));
      },
      async exportExcel() {
        const unconfirmed = this.unconfirmedFilterLabels();
        if (unconfirmed.length) {
          this.$notify({
            showClose: true,
            type: 'warning',
            message: this.$t('stability.timePointExport.unconfirmedFilters', {
              fields: unconfirmed.join(', '),
            }),
          });
          return;
        }
        const loading = this.$loading({
          lock: true,
          text: this.$t('stability.timePointExport.loading'),
          background: 'rgba(48,53,82,0.9)',
        });
        try {
          const response = await stabilityApi.timePointExport(
            this.prepareFormData()
          );
          if (response.status === 204) {
            this.$notify({
              showClose: true,
              type: 'info',
              message: this.$t('stability.timePointExport.noResults'),
            });
            return;
          }
          fileSaver.saveAs(
            response.data,
            `estrazione-T-stabilita_${moment().format('YYYYMMDD')}.xlsx`
          );
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t('stability.timePointExport.error'),
          });
        } finally {
          loading.close();
        }
      },
    },
    created() {
      this.formData = Object.keys(this.forms[FORM_KEY]).length
        ? { ...initialFormData(), ...this.forms[FORM_KEY] }
        : initialFormData();
    },
  };
</script>

<style lang="scss" scoped>
  .el-autocomplete {
    width: 100%;
  }
</style>
